import React, { useState, useEffect } from 'react';
import api from '../services/api'; // Importa o serviço de API configurado
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Registrar os componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Componente para exibir estatísticas gerais
const EstatisticasGerais = ({ stats }) => {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF]">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-600">Videoaulas</p>
                        <p className="text-3xl font-bold text-blue-500">{stats.totalVideoaulasAssistidas}</p>
                    </div>
                    <div className="bg-blue-100 p-3 rounded-full">
                        <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                        </svg>
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF]">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-600">Flashcards criados</p>
                        <p className="text-3xl font-bold text-yellow-500">{stats.totalFlashcardsCriados}</p>
                    </div>
                    <div className="bg-yellow-100 p-3 rounded-full">
                        <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Componente para exibir gráfico de evolução da taxa de acerto e número de questões
const GraficoEvolucaoAcerto = ({ evolucao }) => {
    // Preparar dados para o gráfico e ordenar por data (mais antiga primeiro, mais recente por último)
    const dadosOrdenados = evolucao && evolucao.length > 0 
        ? [...evolucao].sort((a, b) => new Date(a.data) - new Date(b.data))
        : [];
        
    const datas = dadosOrdenados.length > 0 
        ? dadosOrdenados.map(dia => new Date(dia.data).toLocaleDateString('pt-BR'))
        : [new Date().toLocaleDateString('pt-BR')]; // Mostrar data atual se não houver dados
        
    const taxasAcerto = dadosOrdenados.length > 0 
        ? dadosOrdenados.map(dia => dia.percentualAcertos)
        : [0]; // Valor zero se não houver dados
        
    const questoesFeitas = dadosOrdenados.length > 0 
        ? dadosOrdenados.map(dia => dia.totalQuestoes)
        : [0]; // Valor zero se não houver dados

    const data = {
        labels: datas,
        datasets: [
            {
                label: 'Taxa de Acerto (%)',
                data: taxasAcerto,
                borderColor: '#7B2CDB',
                backgroundColor: 'rgba(123, 44, 219, 0.1)',
                borderWidth: 2,
                pointRadius: dadosOrdenados.length > 0 ? 4 : 0, // Ocultar pontos se não houver dados reais
                pointBackgroundColor: '#7B2CDB',
                pointBorderColor: '#fff',
                pointHoverRadius: dadosOrdenados.length > 0 ? 6 : 0,
                fill: true,
                tension: 0.4
            },
            {
                label: 'Questões Feitas',
                data: questoesFeitas,
                borderColor: '#9B4DFF',
                backgroundColor: 'rgba(155, 77, 255, 0.1)',
                borderWidth: 2,
                pointRadius: dadosOrdenados.length > 0 ? 4 : 0, // Ocultar pontos se não houver dados reais
                pointBackgroundColor: '#9B4DFF',
                pointBorderColor: '#fff',
                pointHoverRadius: dadosOrdenados.length > 0 ? 6 : 0,
                fill: true,
                tension: 0.4,
                yAxisID: 'y1'
            }
        ]
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Evolução da Taxa de Acerto e Número de Questões'
            },
            tooltip: {
                mode: 'index',
                intersect: false
            }
        },
        scales: {
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Taxa de Acerto (%)'
                },
                min: 0,
                max: 100
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: {
                    display: true,
                    text: 'Número de Questões'
                },
                grid: {
                    drawOnChartArea: false,
                },
                min: 0
            },
            x: {
                title: {
                    display: true,
                    text: 'Data'
                }
            }
        },
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        }
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] mb-8">
            <Line data={data} options={options} />
        </div>
    );
};

// Componente para exibir gráfico de evolução de videoaulas assistidas
const GraficoEvolucaoVideoaulas = ({ evolucao }) => {
    // Ordenar os dados por data (do mais antigo para o mais recente)
    const evolucaoOrdenada = evolucao && evolucao.length > 0 
        ? [...evolucao].sort((a, b) => new Date(a.data) - new Date(b.data))
        : [];
    
    // Usar os dados reais de videoaulas assistidas
    const datas = evolucaoOrdenada.length > 0 
        ? evolucaoOrdenada.map(dia => new Date(dia.data).toLocaleDateString('pt-BR'))
        : [new Date().toLocaleDateString('pt-BR')]; // Mostrar data atual se não houver dados
    
    const videoaulasAssistidas = evolucaoOrdenada.length > 0 
        ? evolucaoOrdenada.map(dia => dia.videoaulasAssistidas || 0)
        : [0]; // Valor zero se não houver dados

    const data = {
        labels: datas,
        datasets: [
            {
                label: 'Videoaulas Assistidas',
                data: videoaulasAssistidas,
                borderColor: '#2196F3', // Azul
                backgroundColor: 'rgba(33, 150, 243, 0.1)', // Azul claro
                borderWidth: 2,
                pointRadius: evolucaoOrdenada.length > 0 ? 4 : 0, // Ocultar pontos se não houver dados reais
                pointBackgroundColor: '#2196F3', // Azul
                pointBorderColor: '#fff',
                pointHoverRadius: evolucaoOrdenada.length > 0 ? 6 : 0,
                fill: true,
                tension: 0.4
            }
        ]
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Evolução de Videoaulas Assistidas'
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Número de Videoaulas'
                }
            },
            x: {
                title: {
                    display: true,
                    text: 'Data'
                }
            }
        }
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] mb-8">
            <Line data={data} options={options} />
        </div>
    );
};

// Componente para exibir gráfico de evolução de flashcards para revisar
const GraficoEvolucaoFlashcards = ({ evolucao }) => {
    // Usar os dados reais de flashcards não revisados
    const datas = evolucao && evolucao.length > 0
        ? evolucao.map(dia => new Date(dia.data).toLocaleDateString('pt-BR'))
        : [new Date().toLocaleDateString('pt-BR')]; // Mostrar data atual se não houver dados

    // Usar dados de flashcards criados
    const flashcardsCriados = evolucao && evolucao.length > 0
        ? evolucao.map(dia => dia.flashcardsCriados || 0)
        : [0]; // Valor zero se não houver dados

    const data = {
        labels: datas,
        datasets: [
            {
                label: 'Flashcards criados',
                data: flashcardsCriados,
                borderColor: '#FFC107', // Amarelo
                backgroundColor: 'rgba(255, 193, 7, 0.1)', // Amarelo claro
                borderWidth: 2,
                pointRadius: evolucao && evolucao.length > 0 ? 4 : 0, // Ocultar pontos se não houver dados reais
                pointBackgroundColor: '#FFC107', // Amarelo
                pointBorderColor: '#fff',
                pointHoverRadius: evolucao && evolucao.length > 0 ? 6 : 0,
                fill: true,
                tension: 0.4
            }
        ]
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Evolução de Flashcards criados'
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Número de Flashcards criados'
                }
            },
            x: {
                title: {
                    display: true,
                    text: 'Data'
                }
            }
        }
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] mb-8">
            <Line data={data} options={options} />
        </div>
    );
};

// Componente para exibir progresso por matéria
const ProgressoPorMateria = ({ progressoDetalhado }) => {
    const [materiaExpandida, setMateriaExpandida] = useState(null);

    if (!progressoDetalhado || !Array.isArray(progressoDetalhado) || progressoDetalhado.length === 0) {
        return (
            <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF]">
                <h3 className="text-xl font-semibold mb-4">Progresso por Matéria</h3>
                <p className="text-gray-500">Nenhum progresso por matéria disponível ainda.</p>
            </div>
        );
    }

    return (
        <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] mb-8">
            <h3 className="text-xl font-semibold mb-6">Progresso por Matéria</h3>
            <div className="space-y-6">
                {progressoDetalhado.map((materia, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg">
                        <div 
                            className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => setMateriaExpandida(materiaExpandida === index ? null : index)}
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="font-semibold text-gray-800">{materia.materiaNome || 'Matéria sem nome'}</h4>
                                    <p className="text-sm text-gray-600">
                                        {materia.totalQuestoes || 0} questões • {materia.totalAcertos || 0} acertos
                                    </p>
                                </div>
                                <div className="flex items-center">
                                    <div className="w-24 bg-gray-200 rounded-full h-2 mr-4">
                                        <div 
                                            className="bg-[#9B4DFF] h-2 rounded-full transition-all duration-300" 
                                            style={{ width: `${materia.percentualAcertos || 0}%` }}
                                        ></div>
                                    </div>
                                    <span className="font-semibold text-[#7B2CDB] w-12">
                                        {materia.percentualAcertos || 0}%
                                    </span>
                                    <svg 
                                        className={`w-5 h-5 ml-2 transition-transform ${materiaExpandida === index ? 'rotate-180' : ''}`} 
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>
                        
                        {materiaExpandida === index && materia.topicos && Array.isArray(materia.topicos) && (
                            <div className="px-4 pb-4 border-t border-gray-200 bg-gray-50">
                                <div className="space-y-3 mt-4">
                                    {materia.topicos.map((topico, topicoIndex) => (
                                        <div key={topicoIndex} className="ml-4">
                                            <div className="flex items-center justify-between py-2">
                                                <div>
                                                    <h5 className="font-medium text-gray-700">{topico.topicoNome || 'Tópico sem nome'}</h5>
                                                    <p className="text-xs text-gray-500">
                                                        {topico.totalQuestoes || 0} questões • {topico.totalAcertos || 0} acertos
                                                    </p>
                                                    {/* Barra de progresso de videoaulas */}
                                                    {topico.videoaulas && (
                                                        <div className="mt-2">
                                                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                                                                <span>Videoaulas concluídas</span>
                                                                <span>{topico.videoaulasAssistidas || 0}/{topico.totalVideoaulas || 0}</span>
                                                            </div>
                                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                                <div 
                                                                    className="bg-green-500 h-2 rounded-full" 
                                                                    style={{ width: `${topico.percentualVideoaulas || 0}%` }}
                                                                ></div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center">
                                                    <div className="w-20 bg-gray-200 rounded-full h-2 mr-3">
                                                        <div 
                                                            className="bg-[#7B2CDB] h-2 rounded-full transition-all duration-300" 
                                                            style={{ width: `${topico.percentualAcertos || 0}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className="text-sm font-medium text-[#7B2CDB] w-10">
                                                        {topico.percentualAcertos || 0}%
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

// Componente principal da página de progresso
const ProgressoPage = ({ onVoltar, user }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasAccess, setHasAccess] = useState(false);
    const [accessChecked, setAccessChecked] = useState(false);

    useEffect(() => {
        const checkAccessAndLoadData = async () => {
            try {
                // Check if user has access to progress section
                const accessResponse = await api.get('/api/section-access/check/progress');
                if (accessResponse.data.success && accessResponse.data.hasAccess) {
                    setHasAccess(true);

                    const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                    if (!userInfo || !userInfo.token) {
                        setError('Usuário não autenticado. Por favor, faça login.');
                        setLoading(false);
                        setAccessChecked(true);
                        return;
                    }

                    const config = {
                        headers: { Authorization: `Bearer ${userInfo.token}` }
                    };

                    // Buscar estatísticas gerais
                    const statsResponse = await api.get('/api/progresso/stats', config);
                    setStats(statsResponse.data);
                } else {
                    setHasAccess(false);
                }
            } catch (err) {
                console.error('Erro ao verificar acesso ou carregar dados de progresso:', err);
                setHasAccess(false);
            } finally {
                setLoading(false);
                setAccessChecked(true);
            }
        };

        checkAccessAndLoadData();
    }, [user]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F8F6FC] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#9B4DFF] mx-auto mb-4"></div>
                    <p className="text-gray-600">Carregando seu progresso...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#F8F6FC] flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-500 mb-4">{error}</p>
                    <button 
                        onClick={() => window.location.reload()} 
                        className="bg-[#9B4DFF] text-white px-4 py-2 rounded-lg hover:bg-[#7B2CDB] transition-colors"
                    >
                        Tentar Novamente
                    </button>
                </div>
            </div>
        );
    }

    // Função para lidar com o upgrade
    const handleUpgrade = () => {
        // Redirecionar para a página de vendas
        onVoltar('venda-premium');
    };

    if (!accessChecked) {
        return (
            <div className="min-h-screen bg-[#F8F6FC] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#9B4DFF] mx-auto mb-4"></div>
                    <p className="text-gray-600">Verificando acesso...</p>
                </div>
            </div>
        );
    }

    if (!hasAccess) {
        return (
            <div className="min-h-screen bg-[#F8F6FC]">
                <div className="container mx-auto p-4 sm:p-6 md:p-8">
                    {/* Header */}
                    <header className="flex items-center justify-between mb-8">
                        <div className="flex items-center">
                            <button
                                onClick={() => onVoltar && onVoltar('home')}
                                className="flex items-center text-[#7B2CDB] font-semibold hover:text-[#9B4DFF] transition-colors mr-6"
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                                </svg>
                                Voltar
                            </button>
                            <h1 className="text-3xl font-bold text-gray-800">Meu Progresso</h1>
                        </div>
                    </header>

                    {/* Conteúdo principal com blur */}
                    <div className="blur-sm pointer-events-none">
                        <EstatisticasGerais stats={stats || {
                            totalVideoaulasAssistidas: 0,
                            totalFlashcardsCriados: 0
                        }} />
                        <GraficoEvolucaoVideoaulas evolucao={stats?.evolucaoTemporal || []} />
                        <GraficoEvolucaoFlashcards evolucao={stats?.evolucaoTemporal || []} />

                        {/* Mensagem caso não tenha dados */}
                        {stats && stats.totalQuestoes === 0 && stats.totalVideoaulasAssistidas === 0 && stats.totalFlashcardsCriados === 0 && (
                            <div className="bg-white p-8 rounded-xl shadow-md border border-[#E0CCFF] text-center mt-8">
                                <div className="mb-4">
                                    <svg className="w-16 h-16 text-gray-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2-2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                                    Comece sua jornada de estudos!
                                </h3>
                                <p className="text-gray-500 mb-6">
                                    Você ainda não respondeu nenhuma questão, assistiu nenhuma videoaula ou criou flashcards. Que tal começar agora?
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Overlay com mensagem de acesso */}
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="text-center p-8 max-w-md bg-white rounded-xl shadow-lg">
                            <h2 className="text-2xl font-bold text-purple-600 mb-4">É necessário atualizar o seu plano para ter acesso</h2>
                            <div className="mb-4">
                                <button
                                    onClick={handleUpgrade}
                                    className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-3 px-6 rounded-full transition-colors duration-300 shadow-lg"
                                >
                                    Atualize seu Plano
                                </button>
                            </div>
                            <button
                              onClick={() => window.history.back()}
                              className="text-gray-600 hover:text-gray-800 font-medium text-sm underline"
                            >
                              Voltar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8F6FC]">
            <div className="container mx-auto p-4 sm:p-6 md:p-8">
                {/* Header */}
                <header className="flex items-center justify-between mb-8">
                    <div className="flex items-center">
                        <button
                            onClick={() => onVoltar && onVoltar('home')}
                            className="flex items-center text-[#7B2CDB] font-semibold hover:text-[#9B4DFF] transition-colors mr-6"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                            </svg>
                            Voltar
                        </button>
                        <h1 className="text-3xl font-bold text-gray-800">Meu Progresso</h1>
                    </div>
                </header>


                {/* Conteúdo principal */}
                <div>
                    <EstatisticasGerais stats={stats} />
                    <GraficoEvolucaoVideoaulas evolucao={stats.evolucaoTemporal} />
                    <GraficoEvolucaoFlashcards evolucao={stats.evolucaoTemporal} />
                </div>

                {/* Mensagem caso não tenha dados */}
                {stats && stats.totalQuestoes === 0 && stats.totalVideoaulasAssistidas === 0 && stats.totalFlashcardsCriados === 0 && (
                    <div className="bg-white p-8 rounded-xl shadow-md border border-[#E0CCFF] text-center mt-8">
                        <div className="mb-4">
                            <svg className="w-16 h-16 text-gray-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2-2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-600 mb-2">
                            Comece sua jornada de estudos!
                        </h3>
                        <p className="text-gray-500 mb-6">
                            Você ainda não respondeu nenhuma questão, assistiu nenhuma videoaula ou criou flashcards. Que tal começar agora?
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProgressoPage;